import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getWalletEntityId } from "../../utils/walletApiUtils";
import {
  createWithdrawBankAccount,
  createWithdrawCryptoAccount,
  createWithdrawUpiAccount,
  fetchWithdrawAccounts,
  fetchWithdrawInit,
  setWithdrawBankTypeId,
  setWithdrawCryptoTypeId,
  setWithdrawSelectedAccountId,
  setWithdrawTab,
  setWithdrawUpiTypeId,
  submitWithdrawRequest,
} from "../../store/slices/withdrawSlice";
import { getBalanceCRefAsync } from "../../store/slices/betSlice";
import "./withdraw.css";

const CRYPTO_OPTIONS = [
  { label: "USDT (TRON)", crypto_currency: "USDT", network_id: "TRC20" },
  { label: "USDT (ERC20)", crypto_currency: "USDT", network_id: "ERC20" },
  { label: "USDT (BEP20)", crypto_currency: "USDT", network_id: "BEP20" },
  { label: "BTC", crypto_currency: "BTC", network_id: "" },
  { label: "ETH", crypto_currency: "ETH", network_id: "" },
  { label: "TRX (TRON)", crypto_currency: "TRX", network_id: "TRON" },
];

function computeCashableAmount(balance) {
  if (balance == null) return 0;

  // Normalize common backend shapes:
  // - number: 1234.56
  // - { balance: 1234.56 }
  // - { data: { availableTotal: 1234.56 } }
  // - { data: { balance: 1234.56 } }
  // - { data: [{ balance: 1234.56 }] } or [{ balance: 1234.56 }]
  const data0 = Array.isArray(balance?.data) ? balance.data[0] : null;
  const root0 = Array.isArray(balance) ? balance[0] : null;

  const candidates = [
    // preferred "cashable"
    balance?.data?.availableTotal,
    data0?.availableTotal,
    root0?.availableTotal,
    balance?.availableTotal,
    // fallbacks
    balance?.data?.balance,
    data0?.balance,
    root0?.balance,
    balance?.balance,
    // other naming seen in some APIs
    balance?.data?.cashableAmount,
    data0?.cashableAmount,
    root0?.cashableAmount,
    balance?.cashableAmount,
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  const n2 = Number(balance);
  return Number.isFinite(n2) ? n2 : 0;
}

const Withdraw = () => {
  const dispatch = useAppDispatch();
  const { balance } = useAppSelector((state) => state.bet || {});
  const {
    walletTypes,
    withdrawMin,
    withdrawMax,
    tab: activeTab,
    selectedBankTypeId,
    selectedCryptoTypeId,
    selectedUpiTypeId,
    selectedAccountId,
    accounts,
    isInitLoading: loadingInit,
    isAccountsLoading: loadingAccounts,
    savingBank,
    savingCrypto,
    savingUpi,
    submittingWithdraw,
  } = useAppSelector((state) => state.withdraw);

  const cashableAmount = useMemo(
    () => computeCashableAmount(balance),
    [balance],
  );

  const bankTypes = useMemo(
    () =>
      walletTypes.filter((t) =>
        String(t?.category || "")
          .toUpperCase()
          .includes("BANK"),
      ),
    [walletTypes],
  );
  const cryptoTypes = useMemo(
    () =>
      walletTypes.filter((t) =>
        String(t?.category || "")
          .toUpperCase()
          .includes("CRYPTO"),
      ),
    [walletTypes],
  );
  const upiTypes = useMemo(
    () =>
      walletTypes.filter((t) =>
        String(t?.category || "")
          .toUpperCase()
          .includes("UPI"),
      ),
    [walletTypes],
  );

  const selectedAccount = useMemo(
    () => accounts.find((a) => getWalletEntityId(a) === selectedAccountId) ?? null,
    [accounts, selectedAccountId],
  );

  const [showBankForm, setShowBankForm] = useState(false);
  const [showUpiForm, setShowUpiForm] = useState(false);
  const [showCryptoForm, setShowCryptoForm] = useState(false);

  const [holderName, setHolderName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [bankTypeSaving, setBankTypeSaving] = useState("SAVING");

  const [upiId, setUpiId] = useState("");
  const [upiPhone, setUpiPhone] = useState("");
  const [upiHolder, setUpiHolder] = useState("");

  const [cryptoOptionIndex, setCryptoOptionIndex] = useState(0);
  const [cryptoWallet, setCryptoWallet] = useState("");
  const [cryptoHolder, setCryptoHolder] = useState("");

  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawRemark, setWithdrawRemark] = useState("");

  useEffect(() => {
    (async () => {
      try {
        await dispatch(fetchWithdrawInit()).unwrap();
      } catch (msg) {
        toast.error(typeof msg === "string" ? msg : "Failed to load withdraw");
      }
    })();
    dispatch(getBalanceCRefAsync());
  }, [dispatch]);

  useEffect(() => {
    const typeId =
      activeTab === "BANK"
        ? selectedBankTypeId
        : activeTab === "CRYPTO"
          ? selectedCryptoTypeId
          : selectedUpiTypeId;
    (async () => {
      try {
        await dispatch(fetchWithdrawAccounts(typeId)).unwrap();
      } catch (msg) {
        toast.error(typeof msg === "string" ? msg : "Failed to load accounts");
      }
    })();
  }, [dispatch, activeTab, selectedBankTypeId, selectedCryptoTypeId, selectedUpiTypeId]);

  useEffect(() => {
    queueMicrotask(() => {
      setShowBankForm(false);
      setShowUpiForm(false);
      setShowCryptoForm(false);
      setWithdrawAmount("");
      setWithdrawRemark("");
    });
  }, [activeTab]);

  const validateBankForm = () => {
    const holder = holderName?.trim();
    const accNum = accountNumber?.trim();
    const ifsc = ifscCode?.trim();
    const bank = bankName?.trim();
    if (!holder || holder.length < 2) {
      toast.error("Account holder name is required");
      return false;
    }
    if (!accNum || !/^\d{9,18}$/.test(accNum)) {
      toast.error("Valid account number (9–18 digits) required");
      return false;
    }
    if (!bank) {
      toast.error("Bank name is required");
      return false;
    }
    if (!ifsc || !/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(ifsc.toUpperCase())) {
      toast.error("Valid IFSC required");
      return false;
    }
    if (!selectedBankTypeId) {
      toast.error("Select a bank account type");
      return false;
    }
    return true;
  };

  const submitBankAccountCreate = async (e) => {
    e.preventDefault();
    if (!validateBankForm()) return;
    try {
      const res = await dispatch(
        createWithdrawBankAccount({
          bank_account_type: selectedBankTypeId,
          holder_name: holderName.trim(),
          bank_name: bankName.trim(),
          account_number: accountNumber.trim(),
          ifsc_code: ifscCode.trim().toUpperCase(),
          bank_type: bankTypeSaving,
        }),
      ).unwrap();
      toast.success(res?.msg || "Saved");
      setShowBankForm(false);
      setHolderName("");
      setAccountNumber("");
      setBankName("");
      setBranchName("");
      setIfscCode("");
      setBankTypeSaving("SAVING");
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to add account");
    }
  };

  const submitCryptoAccountCreate = async (e) => {
    e.preventDefault();
    if (!selectedCryptoTypeId) {
      toast.error("Crypto provider not loaded");
      return;
    }
    if (!cryptoWallet?.trim()) {
      toast.error("Enter wallet address");
      return;
    }
    if (!cryptoHolder?.trim()) {
      toast.error("Enter holder name");
      return;
    }
    const opt = CRYPTO_OPTIONS[cryptoOptionIndex] || CRYPTO_OPTIONS[0];
    try {
      const res = await dispatch(
        createWithdrawCryptoAccount({
          bank_account_type: selectedCryptoTypeId,
          holder_name: cryptoHolder.trim(),
          crypto_coin_type: opt.crypto_currency,
          crypto_wallet: cryptoWallet.trim(),
          network_id: opt.network_id,
        }),
      ).unwrap();
      toast.success(res?.msg || "Saved");
      setShowCryptoForm(false);
      setCryptoWallet("");
      setCryptoHolder("");
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to add wallet");
    }
  };

  const submitUpiAccountCreate = async (e) => {
    e.preventDefault();
    if (!selectedUpiTypeId) {
      toast.error("UPI provider not loaded");
      return;
    }
    if (!upiId?.trim()) {
      toast.error("Enter UPI ID");
      return;
    }
    if (!upiPhone?.trim()) {
      toast.error("Enter mobile number");
      return;
    }
    if (!upiHolder?.trim()) {
      toast.error("Enter account holder name");
      return;
    }
    try {
      const res = await dispatch(
        createWithdrawUpiAccount({
          bank_account_type: selectedUpiTypeId,
          holder_name: upiHolder.trim(),
          upi_id: upiId.trim(),
          upi_phone_number: upiPhone.trim(),
        }),
      ).unwrap();
      toast.success(res?.msg || "Saved");
      setShowUpiForm(false);
      setUpiId("");
      setUpiPhone("");
      setUpiHolder("");
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to add UPI");
    }
  };

  const submitWithdraw = async (e) => {
    e.preventDefault();
    if (!selectedAccount) {
      toast.error("Select an account");
      return;
    }
    const amt = Number(String(withdrawAmount).replace(/,/g, ""));
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (amt < withdrawMin || amt > withdrawMax) {
      toast.error(`Amount must be between ${withdrawMin} and ${withdrawMax}`);
      return;
    }
    if (amt > cashableAmount) {
      toast.error("Insufficient cashable balance");
      return;
    }

    const accId = getWalletEntityId(selectedAccount);
    let body;
    if (activeTab === "CRYPTO") {
      body = {
        payment_method: "CRYPTO_WALLET_TRANSFER",
        currency_type: "INR",
        amount: String(amt),
        mobile_number: "9876543211",
        payment_method_id: accId,
        crypto_currency: selectedAccount.crypto_coin_type,
        network_id: selectedAccount.network_id,
        wallet_address: selectedAccount.crypto_wallet,
      };
    } else {
      body = {
        amount: String(amt),
        remark: withdrawRemark.trim() || "wallet",
        bank_account_id: accId,
      };
    }

    try {
      const res = await dispatch(submitWithdrawRequest({ body })).unwrap();
      toast.success(res?.msg || "Request submitted");
      setWithdrawAmount("");
      setWithdrawRemark("");
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Withdrawal failed");
    }
  };

  const renderProviderSelect = () => {
    const providers =
      activeTab === "BANK" ? bankTypes : activeTab === "UPI" ? upiTypes : cryptoTypes;
    const selected =
      activeTab === "BANK"
        ? selectedBankTypeId
        : activeTab === "UPI"
          ? selectedUpiTypeId
          : selectedCryptoTypeId;
    const onChange = (val) => {
      if (activeTab === "BANK") dispatch(setWithdrawBankTypeId(val));
      else if (activeTab === "UPI") dispatch(setWithdrawUpiTypeId(val));
      else dispatch(setWithdrawCryptoTypeId(val));
    };

    return (
      <div className="withdraw-provider">
        <label className="form-label mt-3">Select Provider</label>
        <select
          className="form-select"
          value={selected}
          onChange={(e) => onChange(e.target.value)}
          disabled={providers.length === 0}
        >
          {providers.length === 0 ? (
            <option value="">No providers</option>
          ) : (
            providers.map((t) => (
              <option key={getWalletEntityId(t)} value={getWalletEntityId(t)}>
                {t.name || getWalletEntityId(t)}
              </option>
            ))
          )}
        </select>
      </div>
    );
  };

  const renderAccounts = () => (
    <>
      <p className="mt-3 mb-2 fw-semibold">Select Payment Option:</p>
      {loadingAccounts ? (
        <p className="text-muted">Loading...</p>
      ) : (
        <div className="withdraw-accounts">
          {accounts.map((acc) => {
            const id = getWalletEntityId(acc);
            const selected = selectedAccountId === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => {
                  dispatch(setWithdrawSelectedAccountId(id));
                  setShowBankForm(false);
                  setShowUpiForm(false);
                  setShowCryptoForm(false);
                }}
                className={`withdraw-account-card ${selected ? "active" : ""}`}
              >
                <div className="fw-semibold text-truncate" style={{ fontSize: 12 }}>
                  {acc.holder_name || "—"}
                </div>
                {activeTab === "BANK" ? (
                  <>
                    <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                      ****{String(acc.account_number ?? "").slice(-4)}
                    </div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {acc.ifsc_code || ""}
                    </div>
                  </>
                ) : activeTab === "UPI" ? (
                  <div
                    className="text-muted"
                    style={{ fontSize: 12, marginTop: 6, wordBreak: "break-all" }}
                  >
                    {acc.upi_id || "—"}
                  </div>
                ) : (
                  <div
                    className="text-muted"
                    style={{ fontSize: 12, marginTop: 6, wordBreak: "break-all" }}
                  >
                    {acc.crypto_coin_type} · {String(acc.crypto_wallet ?? "").slice(0, 8)}…
                  </div>
                )}
              </button>
            );
          })}

          <div
            className="withdraw-add-card"
            role="presentation"
            onClick={() => {
              dispatch(setWithdrawSelectedAccountId(""));
              if (activeTab === "BANK") setShowBankForm(true);
              if (activeTab === "UPI") setShowUpiForm(true);
              if (activeTab === "CRYPTO") setShowCryptoForm(true);
            }}
          >
            <div className="withdraw-add-plus">+</div>
            <div className="mt-2" style={{ fontSize: 13 }}>
              Add New Account
            </div>
          </div>
        </div>
      )}
    </>
  );

  const renderCreateForms = () => {
    if (activeTab === "BANK" && showBankForm) {
      return (
        <form className="withdraw-form" onSubmit={submitBankAccountCreate}>
          <div className="mb-2">
            <label className="form-label">Account Number</label>
            <input
              className="withdraw-input"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
              placeholder="Enter Account Number"
            />
          </div>
          <div className="mb-2">
            <label className="form-label">Account Holder Name</label>
            <input
              className="withdraw-input"
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
              placeholder="Enter Account Holder Name"
            />
          </div>
          <div className="mb-2">
            <label className="form-label">Bank Name</label>
            <input
              className="withdraw-input"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Enter Bank Name"
            />
          </div>
          <div className="mb-2">
            <label className="form-label">Branch Name (optional)</label>
            <input
              className="withdraw-input"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              placeholder="Enter Branch Name"
            />
          </div>
          <div className="mb-2">
            <label className="form-label">IFSC Code</label>
            <input
              className="withdraw-input"
              value={ifscCode}
              onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
              placeholder="Enter IFSC Code"
            />
          </div>
          <div className="mb-2">
            <label className="form-label">Account type</label>
            <select
              className="form-select"
              value={bankTypeSaving}
              onChange={(e) => setBankTypeSaving(e.target.value)}
            >
              <option value="SAVING">SAVING</option>
              <option value="CURRENT">CURRENT</option>
            </select>
          </div>
          <button type="submit" className="btn btn-dark w-100" disabled={savingBank}>
            {savingBank ? "Saving..." : "Save Changes"}
          </button>
        </form>
      );
    }

    if (activeTab === "UPI" && showUpiForm) {
      return (
        <form className="withdraw-form" onSubmit={submitUpiAccountCreate}>
          <div className="mb-2">
            <label className="form-label">UPI ID</label>
            <input
              className="withdraw-input"
              value={upiId}
              onChange={(e) => setUpiId(e.target.value)}
              placeholder="Enter UPI ID"
            />
          </div>
          <div className="mb-2">
            <label className="form-label">Mobile number</label>
            <input
              className="withdraw-input"
              value={upiPhone}
              onChange={(e) => setUpiPhone(e.target.value.replace(/\D/g, ""))}
              placeholder="Enter mobile number"
            />
          </div>
          <div className="mb-2">
            <label className="form-label">Account Holder Name</label>
            <input
              className="withdraw-input"
              value={upiHolder}
              onChange={(e) => setUpiHolder(e.target.value)}
              placeholder="Enter Account Holder Name"
            />
          </div>
          <button type="submit" className="btn btn-dark w-100" disabled={savingUpi}>
            {savingUpi ? "Saving..." : "Save Changes"}
          </button>
        </form>
      );
    }

    if (activeTab === "CRYPTO" && showCryptoForm) {
      return (
        <form className="withdraw-form" onSubmit={submitCryptoAccountCreate}>
          <div className="mb-2">
            <label className="form-label">Select Currency</label>
            <select
              className="form-select"
              value={cryptoOptionIndex}
              onChange={(e) => setCryptoOptionIndex(Number(e.target.value))}
            >
              {CRYPTO_OPTIONS.map((c, i) => (
                <option key={c.label} value={i}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label">Wallet address</label>
            <input
              className="withdraw-input"
              value={cryptoWallet}
              onChange={(e) => setCryptoWallet(e.target.value)}
              placeholder="Enter Wallet Address"
            />
          </div>
          <div className="mb-2">
            <label className="form-label">Account Holder Name</label>
            <input
              className="withdraw-input"
              value={cryptoHolder}
              onChange={(e) => setCryptoHolder(e.target.value)}
              placeholder="Enter Account Holder Name"
            />
          </div>
          <button type="submit" className="btn btn-dark w-100" disabled={savingCrypto}>
            {savingCrypto ? "Saving..." : "Save Changes"}
          </button>
        </form>
      );
    }

    return null;
  };

  return (
    <section className="section listing_page">
      <div className="row justify-content-center">
        <div className="col-12">
          <div className="withdraw-page">
            <div className="card-header game-heading d-flex justify-content-between align-items-center">
              <span>Withdraw</span>
            </div>
            <div className="withdraw-cashable">
              Cashable Amount:{" "}
              {cashableAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="withdraw-body">
              <div className="withdraw-tabs">
                {bankTypes.length > 0 && (
                  <div
                    className={`withdraw-tab ${activeTab === "BANK" ? "active" : ""}`}
                    role="presentation"
                    onClick={() => dispatch(setWithdrawTab("BANK"))}
                  >
                    BANK
                  </div>
                )}
                {upiTypes.length > 0 && (
                  <div
                    className={`withdraw-tab ${activeTab === "UPI" ? "active" : ""}`}
                    role="presentation"
                    onClick={() => dispatch(setWithdrawTab("UPI"))}
                  >
                    UPI
                  </div>
                )}
                {cryptoTypes.length > 0 && (
                  <div
                    className={`withdraw-tab ${activeTab === "CRYPTO" ? "active" : ""}`}
                    role="presentation"
                    onClick={() => dispatch(setWithdrawTab("CRYPTO"))}
                  >
                    CRYPTO
                  </div>
                )}
              </div>

              {loadingInit ? <p className="text-muted">Loading...</p> : null}

              {renderProviderSelect()}
              {renderAccounts()}
              {renderCreateForms()}

              {selectedAccount && (
                <form className="withdraw-form" onSubmit={submitWithdraw}>
                  <p className="text-muted" style={{ fontSize: 12, marginBottom: 10 }}>
                    Min {withdrawMin} — Max {withdrawMax}
                  </p>
                  <div className="mb-2">
                    <label className="form-label">Amount</label>
                    <input
                      className="withdraw-input"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      placeholder="Enter amount"
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Remark</label>
                    <input
                      className="withdraw-input"
                      value={withdrawRemark}
                      onChange={(e) => setWithdrawRemark(e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                  <button className="withdraw-submit" type="submit" disabled={submittingWithdraw}>
                    {submittingWithdraw ? "Submitting..." : "Withdraw"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Withdraw;